import type { AddUserProductInput } from '@front-activerse/common-components';
import { AiDescriptionSource, Ingredient, UnifiedMealData } from '../types';

export const SCAN_LABEL_RESULT_STORAGE_KEY = 'tracker_scan_label_result';
export const SCAN_LABEL_RESULT_EVENT = 'tracker_scan_label_result';

const SCAN_LABEL_CONSUMED_KEY = 'tracker_scan_label_result_consumed';

/**
 * `updatedAt` of the scan result that has already prefilled a form and been saved as a product.
 *
 * The result itself cannot simply be deleted on first read: the "Analyze again" round trip unmounts
 * and remounts the form, which has to find it again. But once its product is saved it must NOT keep
 * prefilling later, unrelated visits — a scan-plate meal, then add-ingredient, then add-product,
 * arrived carrying the previous scan's values.
 *
 * Kept in sessionStorage rather than a module variable so it survives a page reload, which otherwise
 * resurrected the stale prefill (same tab, same stored result, fresh JS).
 */
export const markScanResultConsumed = (updatedAt: number | null): void => {
  try {
    if (updatedAt === null) sessionStorage.removeItem(SCAN_LABEL_CONSUMED_KEY);
    else sessionStorage.setItem(SCAN_LABEL_CONSUMED_KEY, String(updatedAt));
  } catch {
    // Private-mode Safari can throw on storage access; a lost marker only means one stale prefill.
  }
};

export const isScanResultConsumed = (updatedAt: number | null): boolean => {
  if (updatedAt === null) return false;
  try {
    return sessionStorage.getItem(SCAN_LABEL_CONSUMED_KEY) === String(updatedAt);
  } catch {
    return false;
  }
};

const SCAN_LABEL_SETTLED_ENTRIES_KEY = 'tracker_scan_label_settled_entries';

/**
 * `location.key`s of analysis-screen entries whose request has already finished.
 *
 * Needed because the analysis route restarts its request on mount, and a mount is not always a new
 * scan. Going back onto an OLDER analysis entry gets a fresh component instance — SlideRoutes had
 * dropped the original, and the route is keyed on `location.key` — so an in-component ref could not
 * tell "the user just arrived here for the first time" from "the user came back to work that is
 * already done". The latter re-ran the paid analysis and, on the autoScan path, pushed the
 * add-product form again, bouncing the user off the screen they had navigated back to.
 *
 * Keyed by `location.key` (stable per history entry, restored on back) and held in sessionStorage so
 * it survives the remount. Values are never removed: an entry that has settled once can never
 * legitimately need an automatic re-run, and "Analyze again" is an explicit user action that does not
 * consult this list.
 *
 * `'default'` is ignored. React Router falls back to that literal for any entry whose history state
 * carries no key — a directly-opened URL or a reload — so it is shared rather than unique, and
 * treating it as an identity would let one settled visit suppress the analysis on the next.
 */
const DEFAULT_LOCATION_KEY = 'default';

const isUsableEntryKey = (locationKey: string | undefined): locationKey is string =>
  Boolean(locationKey) && locationKey !== DEFAULT_LOCATION_KEY;

export const markScanEntrySettled = (locationKey: string | undefined): void => {
  if (!isUsableEntryKey(locationKey)) return;
  try {
    const settled = readSettledScanEntries();
    if (settled.includes(locationKey)) return;
    // Bounded so a long session cannot grow this without limit; only the most recent entries can
    // still be reached by going back far enough to matter.
    const next = [...settled, locationKey].slice(-10);
    sessionStorage.setItem(SCAN_LABEL_SETTLED_ENTRIES_KEY, JSON.stringify(next));
  } catch {
    // Private-mode Safari can throw on storage access. Losing the marker only costs one repeated
    // analysis, which is the pre-fix behaviour rather than a new failure.
  }
};

export const isScanEntrySettled = (locationKey: string | undefined): boolean => {
  if (!isUsableEntryKey(locationKey)) return false;
  return readSettledScanEntries().includes(locationKey);
};

const readSettledScanEntries = (): string[] => {
  try {
    const raw = sessionStorage.getItem(SCAN_LABEL_SETTLED_ENTRIES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
};

// Each mode ('Per 100' / 'Per serving') owns its OWN independent set of macro inputs.
// Switching modes only swaps which set is shown — values are never recalculated on the fly.
// Prefill from a scan may populate both sets at once (see scanResultToFormValues).
export interface MacroValues {
  calories: string;
  protein: string;
  fat: string;
  carbs: string;
  fiber: string;
}

export interface AddUserProductFormValues {
  name: string;
  brand: string;
  unit: 'GRAMS' | 'MILLILITERS';
  mode: 'PER_100_UNITS' | 'PER_SERVING';
  servingSize: string;
  per100: MacroValues;
  perServing: MacroValues;
}

const emptyMacros: MacroValues = {
  calories: '',
  protein: '',
  fat: '',
  carbs: '',
  fiber: '',
};

export const emptyFormValues: AddUserProductFormValues = {
  name: '',
  brand: '',
  unit: 'GRAMS',
  mode: 'PER_100_UNITS',
  servingSize: '',
  per100: { ...emptyMacros },
  perServing: { ...emptyMacros },
};

export const parseDecimal = (value: string): number => parseFloat(value.replace(',', '.'));

// Atwater calorie factors (USDA), matching ai-flows' nutrition-validator: fiber is indigestible
// carbohydrate at ~2 kcal/g, so digestible carbs = total carbs - fiber.
const ATWATER = { protein: 4, fat: 9, carbs: 4, fiber: 2 } as const;

// Warn (but don't block) when stated calories drift from the macro-derived estimate by more than
// this fraction — deliberately looser than the backend's hard 10% gate.
export const CALORIE_MISMATCH_THRESHOLD = 0.25;

export const calculateCaloriesFromMacros = (macros: MacroValues): number => {
  const protein = parseDecimal(macros.protein) || 0;
  const fat = parseDecimal(macros.fat) || 0;
  const carbs = parseDecimal(macros.carbs) || 0;
  const fiber = parseDecimal(macros.fiber) || 0;
  const digestibleCarbs = Math.max(0, carbs - fiber);
  return (
    protein * ATWATER.protein +
    fat * ATWATER.fat +
    digestibleCarbs * ATWATER.carbs +
    fiber * ATWATER.fiber
  );
};

// Returns the relative calorie mismatch (0..1+) for the ACTIVE mode's set, or null when it can't
// be computed (missing/zero stated calories). Compared against CALORIE_MISMATCH_THRESHOLD to
// decide whether to surface the confirm-again warning.
export const caloriesMismatchRatio = (values: AddUserProductFormValues): number | null => {
  const macros = values.mode === 'PER_SERVING' ? values.perServing : values.per100;
  const stated = parseDecimal(macros.calories);
  if (!Number.isFinite(stated) || stated <= 0) return null;
  const calculated = calculateCaloriesFromMacros(macros);
  return Math.abs(stated - calculated) / stated;
};

export const toMutationInput = (
  values: AddUserProductFormValues,
  ean?: string
): AddUserProductInput => {
  // Send only the macros of the active mode; the backend normalizes per-serving to per-100.
  const macros = values.mode === 'PER_SERVING' ? values.perServing : values.per100;

  // In PER_100_UNITS mode the serving size (if known, e.g. captured from a scan) is sent as
  // standalone product metadata so it isn't lost — the backend ignores it in PER_SERVING mode,
  // where `nutritionValues.servingSize` already carries it.
  const servingQuantity =
    values.mode === 'PER_100_UNITS' && values.servingSize.trim() !== ''
      ? parseDecimal(values.servingSize)
      : null;

  return {
    name: values.name.trim(),
    ...(values.brand.trim() !== '' && { brand: values.brand.trim() }),
    ...(ean && { ean }),
    unit: values.unit,
    nutritionValues: {
      mode: values.mode,
      ...(values.mode === 'PER_SERVING' && { servingSize: parseDecimal(values.servingSize) }),
      calories: parseDecimal(macros.calories),
      protein: parseDecimal(macros.protein),
      fat: parseDecimal(macros.fat),
      carbohydrates: parseDecimal(macros.carbs),
      ...(macros.fiber.trim() !== '' && { fiber: parseDecimal(macros.fiber) }),
    },
    ...(servingQuantity != null &&
      Number.isFinite(servingQuantity) &&
      servingQuantity > 0 && { servingQuantity }),
  };
};

export interface ScanPrimaryIngredient {
  productId?: string;
  name: string;
  brand?: string;
  baseAmount: number;
  baseUnit: string;
  calories: number | null;
  proteinGrams: number | null;
  carbsGrams: number | null;
  fatGrams: number | null;
  fiberGrams?: number | null;
  servingQuantity?: number | null;
  servingUnit?: string | null;
}

const toFormNumber = (value: number | null | undefined): string =>
  value == null ? '' : String(value);

// Round a per-serving macro to at most 2 decimals and drop a trailing ".00"/".x0".
const scaleMacro = (per100: number | null | undefined, factor: number): string => {
  if (per100 == null) return '';
  return String(Math.round(per100 * factor * 100) / 100);
};

export const scanResultToFormValues = (
  primary: ScanPrimaryIngredient,
  current: AddUserProductFormValues
): AddUserProductFormValues => {
  const unit: AddUserProductFormValues['unit'] =
    primary.baseUnit === 'milliliters' ? 'MILLILITERS' : 'GRAMS';

  const per100: MacroValues = {
    calories: toFormNumber(primary.calories) || current.per100.calories,
    protein: toFormNumber(primary.proteinGrams) || current.per100.protein,
    fat: toFormNumber(primary.fatGrams) || current.per100.fat,
    carbs: toFormNumber(primary.carbsGrams) || current.per100.carbs,
    fiber: toFormNumber(primary.fiberGrams) || current.per100.fiber,
  };

  // Fill the per-serving set only when the label gave a serving size in the SAME unit as the
  // per-100 values — otherwise the scaling factor would be meaningless. Values are computed
  // once here (per100 * serving/100), never recalculated live on mode switch.
  const servingUnit = primary.servingUnit === 'milliliters' ? 'MILLILITERS' : 'GRAMS';
  const canScaleServing =
    primary.servingQuantity != null &&
    primary.servingQuantity > 0 &&
    primary.servingUnit != null &&
    servingUnit === unit;

  const perServing: MacroValues = canScaleServing
    ? {
        calories: scaleMacro(primary.calories, primary.servingQuantity! / 100),
        protein: scaleMacro(primary.proteinGrams, primary.servingQuantity! / 100),
        fat: scaleMacro(primary.fatGrams, primary.servingQuantity! / 100),
        carbs: scaleMacro(primary.carbsGrams, primary.servingQuantity! / 100),
        fiber: scaleMacro(primary.fiberGrams, primary.servingQuantity! / 100),
      }
    : current.perServing;

  return {
    ...current,
    name: primary.name || current.name,
    brand: primary.brand || current.brand,
    unit,
    mode: 'PER_100_UNITS',
    servingSize: canScaleServing ? toFormNumber(primary.servingQuantity) : current.servingSize,
    per100,
    perServing,
  };
};

const UNIT_META: Record<'GRAMS' | 'MILLILITERS', { suffix: string; measurementUnit: string }> = {
  GRAMS: { suffix: 'g', measurementUnit: 'grams' },
  MILLILITERS: { suffix: 'ml', measurementUnit: 'milliliters' },
};

export const addedProductToIngredient = (
  added: { id: string | number; name: string; brand?: string | null },
  values: AddUserProductFormValues,
  multiplier: number
): Ingredient => {
  const unitMeta = UNIT_META[values.unit];

  // Determine the default amount + macros that land on the meal ingredient list.
  // - PER_SERVING mode: use the serving values as entered.
  // - PER_100_UNITS mode: default to 100 units UNLESS a serving size was recognized (e.g. from a
  //   scan) — then a real serving is a more useful default than a flat 100g, so scale the per-100
  //   macros down to that serving. The product is still stored per-100 in the backend; this only
  //   affects the pre-filled amount for THIS meal entry.
  const servingSize = parseDecimal(values.servingSize);
  const hasRecognizedServing =
    values.mode === 'PER_100_UNITS' && Number.isFinite(servingSize) && servingSize > 0;

  const macros = values.mode === 'PER_SERVING' ? values.perServing : values.per100;
  const baseAmount =
    values.mode === 'PER_SERVING' ? servingSize : hasRecognizedServing ? servingSize : 100;
  // Factor to convert the per-100 macros to the chosen serving (1 when we keep the 100-unit basis).
  const macroScale = hasRecognizedServing ? servingSize / 100 : 1;

  const amountFor1Portion = baseAmount / multiplier;

  return {
    id: String(added.id),
    // The created product's id comes back in the same matched-products format the search returns
    // (`brand-<id>`), so the meal links to it exactly as if it had been picked from the search list.
    productId: String(added.id),
    name: added.name,
    ...(added.brand && { brand: added.brand }),
    // Always true here: this form just created the product in the user's own database, so the meal
    // it lands on must show the badge straight away — not only after re-picking it from the search
    // list on a later visit (IP-11106).
    isUserProduct: true,
    kcal: (parseDecimal(macros.calories) * macroScale) / multiplier,
    measurements: [
      {
        amount: amountFor1Portion,
        text: `${amountFor1Portion} ${unitMeta.suffix}`,
        unit: unitMeta.measurementUnit,
      },
    ],
    macro: {
      protein: { grams: (parseDecimal(macros.protein) * macroScale) / multiplier },
      fat: { grams: (parseDecimal(macros.fat) * macroScale) / multiplier },
      carbohydrates: { grams: (parseDecimal(macros.carbs) * macroScale) / multiplier },
      // Fiber is optional in the form — an empty field parses to NaN, which means none
      fiber: { grams: ((parseDecimal(macros.fiber) || 0) * macroScale) / multiplier },
    },
  };
};

// Wraps a single freshly-created product (as an Ingredient) into a brand-new meal, so that
// adding a product straight from the standalone barcode scanner (no meal in progress) lands the
// user on the meal editor with that product as the sole ingredient — mirroring the found-product
// path (normalizeBarcodeMeal). The meal name defaults to the product name.
//
// Scan label enters here too, and both entries are AI scans — hence `ai_description`, which is what
// `normalizeDescribeMeal` (scan plate) and `normalizeBarcodeMeal` already use. The value rides
// `mealData.sourceType` through the addMeal mutation and comes back on the tracker row, where
// `TrackerMealComponent.renderSourceType` turns it into the AI badge. It used to say 'manual',
// which rendered scan-label meals as "your meal" — the label a hand-typed meal gets (IP-11147).
export const addedProductToMealData = (
  ingredient: Ingredient,
  dietMealId: number,
  // Which scanner sent the user here — the two entries share this branch, and `ai_description`
  // alone cannot tell a label scan from a barcode scan.
  aiDescriptionSource?: AiDescriptionSource
): UnifiedMealData => ({
  id: dietMealId,
  name: ingredient.name,
  kcal: ingredient.kcal,
  macro: {
    protein: { grams: ingredient.macro?.protein.grams ?? 0 },
    fat: { grams: ingredient.macro?.fat.grams ?? 0 },
    carbohydrates: { grams: ingredient.macro?.carbohydrates.grams ?? 0 },
  },
  preparationImageUrl: '',
  sourceType: 'ai_description',
  aiDescriptionSource,
  ingredients: [ingredient],
  portions: 1,
  isFromBaseMeal: false,
});

// Maps a backend ValidationException fieldName to the matching Formik field path. Non-macro
// fields map directly; macros are submitted from the active mode's set only, so their errors
// resolve to `${mode}.field` (per100/perServing).
const FIELD_NAME_MAP: Record<string, string> = {
  name: 'name',
  brand: 'brand',
  ean: 'name', // ean is not an input; surface its error on the name field
  unit: 'unit',
  servingSize: 'servingSize',
};

const MACRO_FIELD_MAP: Record<string, keyof MacroValues> = {
  calories: 'calories',
  protein: 'protein',
  fat: 'fat',
  carbohydrates: 'carbs',
  fiber: 'fiber',
};

export const resolveErrorField = (
  backendFieldName: string,
  mode: AddUserProductFormValues['mode']
): string | null => {
  if (FIELD_NAME_MAP[backendFieldName]) return FIELD_NAME_MAP[backendFieldName];
  const macroField = MACRO_FIELD_MAP[backendFieldName];
  if (macroField) {
    const group = mode === 'PER_SERVING' ? 'perServing' : 'per100';
    return `${group}.${macroField}`;
  }
  return null;
};

/**
 * Flattens Formik's error object into field paths ("name", "per100.calories").
 *
 * Formik nests errors to mirror the values shape, so the per-mode macro groups arrive as
 * `{ per100: { calories: '...' } }`. Only leaf strings are field paths — a group key on its own
 * matches no input.
 */
export const collectErrorFieldPaths = (errors: unknown, prefix = ''): string[] => {
  if (!errors || typeof errors !== 'object') return [];
  return Object.entries(errors as Record<string, unknown>).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') return value ? [path] : [];
    return collectErrorFieldPaths(value, path);
  });
};
